import { ApiError } from 'api/ApiClient';
import { HTTPStatusCode } from 'config/const';
import Utils from 'lib/Utils';
import { IApiError, IApiErrorPayloadWithCode } from 'types';

interface IApiTransformVars {
  [key: string]: string;
}

const apiVariables: IApiTransformVars = {
  verify_code: 'Verification Code',
  existing_password: 'Current password',
  organization_id: 'Organization id',
  invite_recipient: 'Invite recipient',
  date_of_birth: 'Date of birth',
  user: 'Employee',
  name: 'Name',
  resource: 'This resource',
  token: 'Token',
  grace_period_policy: 'grace_period_policy',
  business_day_start: 'business_day_start',
  payment_date: 'payment_date', // Used for duplicatePayment and dateAlreadyPaid error messages.
};

const apiErrorMessage: IApiTransformVars = {
  'invalid code': 'invalid. Please try again or request a new code.',
  unauthorized: 'Email and password combination incorrect',
  Unauthorized: 'Email and password combination incorrect',
  'invalid user password': 'is incorrect',
  'an employee already exists for this user': 'already exists for this user',
  'Employee is already associated to the site': 'already has this employee associated with it',
  'Insufficient funds for site': 'Sorry, this site currently has insufficient funds to fulfill your request. Please contact your organization administrator or Kickfin for assistance.',
};

interface IApiErrorWithCode {
  errors: IApiError[];
  statusCode?: string;
  requestMethod?: string;
  requestName?: string;
}

interface IApiCustomMessagesType {
  param: string;
  msg: string;
  customMsg: string;
}

const apiCustomMessages: IApiCustomMessagesType[] = [
  {
    param: 'identity',
    msg: 'not found',
    customMsg: 'This combination of contact information and date of birth does not match any account on file.',
  },
  {
    param: 'identity',
    msg: 'already exists',
    customMsg: 'This contact is already in use on another account.',
  },
];

const getCustomErrorMessage = (param: string, msg: string, statusCode: number, remaining_attempts: number) => {
  let customMsg = '';

  if (statusCode === 423) {
    customMsg = 'This account is temporarily locked due to too many failed login attempts. Please try again later.';
  }

  if (!customMsg && remaining_attempts && remaining_attempts < 4) {
    customMsg = `Email and password combination incorrect. You have ${remaining_attempts} attempt${remaining_attempts > 1 ? 's' : ''} remaining before your account is locked.`;
  }

  if (!customMsg) {
    customMsg = apiCustomMessages.find((apiCustomMessage) => apiCustomMessage.param === param && apiCustomMessage.msg === msg)?.customMsg;
  }

  return customMsg;
};

/**
 * This function formats error parameters, messages and values returned from the api and transforms them into predefined error messages
 * @param {IApiErrorWithCode} apiResponse - The response object containing:
 *    - `errors`: An array of error objects.
 *    - `statusCode`: The error's status code.
 *    - `requestMethod`: HTTP request method of the request that triggered the error.
 *    - `requestName`: Name of the request that triggered the error.
 * @param {boolean} includeParam - Includes param value in the error message
 * @returns {IApiErrorPayloadWithCode} - An object containing:
 *    - `errorMessages`: An array of formatted error messages. Each message combines the transformed `param` and `msg` from the input `apiResponse.errors`.
 *                       If the function encounters an error, it logs the error and returns a default error message.
 *    - `id`: The ID associated with the first error in the input `apiResponse.errors`. If the function encounters an error, the requestMethod and the requestName are returned.
 *    - `code`: The first error `code` in the input `apiResponse.errors` (for example `pay_period_closed`). Left out when no error has one.
 *    - `statusCode`: The status code from the input `apiResponse` as a string. If the function encounters an error, it returns 500
 */
export const formatErrorMessages = (apiResponse: IApiErrorWithCode, includeParam = false): IApiErrorPayloadWithCode => {
  try {
    const apiErrors = apiResponse.errors.map(({ param, value, msg, remainingAttempts }: IApiError) => {
      const customMsg = getCustomErrorMessage(param, msg, parseInt(apiResponse.statusCode, 10), remainingAttempts);
      if (customMsg) return customMsg;

      const transformedParam = param && (apiVariables[param] || (includeParam && Utils.camelCaseKey(param)));
      const transformedMsg = msg ? apiErrorMessage[msg] || msg : '';

      return [transformedParam, value, transformedMsg].filter(Boolean).join(' ');
    });
    const code = apiResponse.errors.find((err) => err.code)?.code;
    return {
      errorMessages: apiErrors.join('').trim() ? apiErrors : ['An error occurred. Please try again later.'],
      id: apiResponse.errors.find((err) => err.id)?.id,
      ...(code && { code }),
      statusCode: apiResponse.statusCode?.toString(),
    };
  } catch (err) {
    return {
      errorMessages: ['An error occurred. Please try again later.'],
      statusCode: apiResponse?.statusCode || '500',
    };
  }
};

/**
 * Rewrites the credential-flow copy in `formatErrorMessages` output into verification-code
 * wording, for screens where the credentials passed and only the code was wrong. Anything
 * without a rewrite passes through untouched.
 */
export const formatVerificationCodeError = (error: string): string => {
  const msg = error.toLowerCase().trim();

  if (msg === 'code has expired and is no longer valid') {
    return 'The validation code is no longer valid, please click back and login again';
  }

  if (msg === 'email and password combination incorrect' || msg === 'verification code invalid verify code') {
    return 'The validation code is invalid. Please try again or request a new code.';
  }

  if (msg.includes('email and password combination incorrect. you have')) {
    const attemptsRemaining = error.replace('Email and password combination incorrect.', '').trim();

    return `The validation code is invalid. Please try again or request a new code. ${attemptsRemaining}`;
  }

  return error;
};

/**
 * Detects the "POS integration not found" API error and returns a user-facing message for it.
 *
 * @param {ApiError} error - The caught API error to inspect.
 * @returns {{ message: string } | undefined} A message object when the error is "POS integration not found", otherwise `undefined`.
 */
export const getIntegrationNotFoundError = (error: ApiError): { message: string } | undefined => {
  const err = error?.body?.errors?.[0];
  const isIntegrationNotFound = error?.statusCode === HTTPStatusCode.NOT_FOUND && err?.param === 'integrations' && err.msg === 'not found';
  if (!isIntegrationNotFound) return undefined;
  return { message: 'This site does not have a valid point of sale integration. Please contact support to enable the integration.' };
};

export const PAY_PERIOD_CLOSED_CODE = 'pay_period_closed';

/**
 * Returns the API message when account_services refuses a dismiss, remove or delete because the employee has
 * pending or expired payments in a closed pay period. The message names the site and period.
 *
 * @param {ApiError | IApiErrorPayloadWithCode} error - The stored request error: a raw ApiError, or the output of `formatErrorMessages`.
 * @returns {string | undefined} The API message when the error is `pay_period_closed`, otherwise `undefined`.
 */
export const getPayPeriodClosedMessage = (error: ApiError | IApiErrorPayloadWithCode): string | undefined => {
  if (!error) return undefined;
  if ('errorMessages' in error) return error.code === PAY_PERIOD_CLOSED_CODE ? error.errorMessages[0] : undefined;
  const errors: IApiError[] = Array.isArray(error.body?.errors) ? error.body.errors : [];
  return errors.find((err) => err?.code === PAY_PERIOD_CLOSED_CODE)?.msg;
};

/**
 * Splits the `pay_period_closed` API message for the web modal, whose title already says "Unable to <verb> employee".
 * Drops that leading sentence and puts the next step ("Reopen the pay period, ...") in its own paragraph. A message in
 * any other shape comes back whole as `reason`, so nothing is lost if the API wording changes.
 *
 * @param {string} message - The API message.
 * @returns {{ reason: string; nextStep?: string }} The reason the action was blocked, and the next step when found.
 */
export const splitPayPeriodClosedMessage = (message: string): { reason: string; nextStep?: string } => {
  const withoutTitle = message.replace(/^Unable to \w+ employee\.\s*/, '');
  const nextStepAt = withoutTitle.indexOf('Reopen the pay period');
  if (nextStepAt <= 0) return { reason: withoutTitle || message };
  return { reason: withoutTitle.slice(0, nextStepAt).trim(), nextStep: withoutTitle.slice(nextStepAt).trim() };
};
